import { isPrefilledWeightSource } from '@heraion/shared';
import { emptySet, withEntry } from './loggerDraftHelpers';
import type { LoggerAction, LoggerDraft } from './loggerDraftTypes';
import { kindNeedsDuration } from '../deriveEntryKind';

// The exercise SWAP, split out of loggerNestedReducer.ts (#355) when it had
// to learn one more prescription field and that file sat at 476 lines. Same
// seam as the group and session reducers there: the parent narrows the union
// and hands the action over, behaviour unchanged.

export type LoggerSwapAction = Extract<LoggerAction, { type: 'replace_exercise' }>;

export function loggerSwapReducer(
  state: LoggerDraft,
  action: LoggerSwapAction,
): LoggerDraft {
  return withEntry(state, action.entryIdx, (entry) => {
    // A cross-kind swap (reps-family ↔ duration-family) orphans the set's
    // already-typed field, which would be silently dropped at save (IB-6).
    // Reset the sets to blank so nothing is lost quietly, the UI confirms
    // first when there's logged data to clear. Same-kind swaps keep the
    // logged sets (panchina occupata: bench → db bench).
    const crosses =
      kindNeedsDuration(entry) !== kindNeedsDuration(action);
    // EQ-1-TAGSTART: the same action re-stamps a MACHINE on the exercise
    // already there (§8.4: naming a machine is a same-exercise replace,
    // one gesture, not two). Then nothing about the sets is stale: the
    // prescription, the prescribed effort and the athlete's note all still
    // describe this exercise, because it is still this exercise. The reset
    // below exists because the exercise CHANGED, so it must not run when
    // it did not. Inbox f2881eaa (2026-08-20): naming a machine emptied
    // both the prescribed and the history weight, and the athlete was left
    // with no starting load exactly where they needed one.
    const isSameExercise = action.exerciseId === entry.exerciseId;
    // The catalog-tag snapshot describes the old exercise: it goes with it.
    // #353: so does a calibration, offered or running.
    const { tags: oldTags, calibration: oldCalibration, ...kept } = entry;
    return {
      ...kept,
      ...(isSameExercise && oldTags ? { tags: oldTags } : {}),
      ...(isSameExercise && oldCalibration ? { calibration: oldCalibration } : {}),
      exerciseId: action.exerciseId,
      exerciseName: action.exerciseName,
      isBodyweight: action.isBodyweight,
      // Adopt the new exercise's logging kind so the UI (hold timer vs
      // reps/weight vs cardio) and the saved set kind follow the swap.
      // IB-2: previously stale → treadmill→hold stayed cardio, weighted→
      // hold stayed weighted.
      isCardio: action.isCardio,
      isTimeBased: action.isTimeBased,
      movementPattern: action.movementPattern,
      // EQ-1: the swap picker is the one surface that asks which
      // machine. `undefined` means it had nothing to say (no instances
      // known), and must NOT wipe an instance the entry already
      // carries, the athlete swapping bench→db-bench is not telling
      // us the machine became unknown.
      equipmentInstance:
        action.equipmentInstance === undefined
          ? entry.equipmentInstance
          : action.equipmentInstance,
      // IB-8: adopt the new exercise's laterality seed too, same
      // completed-sets-stay-honest rule as set_entry_loaded_sides.
      loadedSides: action.loadedSides,
      // IB-3: the plan cues (💬 instruction, 🎯 per-set) are no longer in
      // the draft, they resolve per-slug from usePlanCues, so the swap
      // shows the new exercise's cues by construction.
      // ⭐ PE-37 note, and it is the invariant that keeps this spread safe:
      // `DraftEntry.notePrescribed` IS in the draft, and this `{...entry}`
      // would carry it onto the swapped-in exercise. It never bites because
      // only `slotDraftToEntry` (the prescribe read adapter) sets it, and
      // the prescribe tree swaps through `replace_exercise` in
      // prescribeDispatch, not through this reducer. ⛔ If a logger entry
      // ever gains that field, this line has to reset it, a note about
      // squats has no business surviving onto a leg press. What still describes
      // the OLD exercise on a kept set is: the plan's PREFILL (prescribed
      // rpe/rir, AMRAP from the old slot, weight provenance vs the old
      // exercise's prescription/MAX) plus the athlete's per-set note. On a
      // NOT-YET-LOGGED set all of that is now wrong; a completed set is
      // logged history and stays untouched (never rewrite the athlete's real
      // log). A cross-kind swap blanks the sets instead. A chain-prefilled
      // weight (prescription / %MAX / history, isPrefilledWeightSource)
      // is blanked with it: it priced the OLD exercise, and on a barbell →
      // two-dumbbell swap it would silently double (40 kg bench carried
      // onto a db press reads as 40 per implement = 80 total). A weight
      // the athlete TYPED ('manual'/absent) survives, panchina occupata:
      // they planned those numbers.
      sets: isSameExercise
        ? // Only the gear they are marked on changes, which is exactly
          // what the band above the tray promises the athlete, in all five
          // locales (`swap.currentConsequence`). The tag lives on the
          // ENTRY, so the sets need no rewrite at all to carry it.
          entry.sets
        : crosses
        ? entry.sets.map((s) => emptySet(s.unit, action.loadedSides))
        : entry.sets.map((s, i) => {
            // A completed set is history: untouched, calibration flag
            // included (#353).
            if (s.completedAt !== null) return s;
            // EQ-1-SWAP-PCTMAX: what the cell should say instead of
            // going blank, already resolved (unit converted, floored to
            // a loadable rung) by the dispatch site. `undefined` here is
            // the pre-EQ-1-SWAP-PCTMAX behaviour, unchanged.
            const carried = action.carriedWeights?.[i] ?? null;
            return {
              ...s,
              note: '',
              rpe: '',
              rir: '',
              // BOTH halves reset, for different reasons: the
              // prescription described the OLD slot (the new
              // exercise must not inherit a request nobody made),
              // and this branch only runs on completedAt === null,
              // so there is no performed outcome to keep either.
              amrapPrescribed: false,
              amrapFloor: undefined,
              amrap: false,
              // ⚠️ The same rule, and it was missed once already: this
              // branch spreads `...s`, so EVERY prescription field has to
              // be listed here by name or it rides the swap. HI-1's time
              // axis was the second pair to land on this shape, and for a
              // day a swapped-in exercise inherited a 30s bout and a 90s
              // rest nobody wrote for it, silently, because a same-kind
              // swap raises no warning. If you add a third prescription
              // field, it belongs in this list on the same commit.
              workPrescribed: undefined,
              restPrescribedSec: undefined,
              repsRangePrescribed: undefined,
              amrapStopPrescribed: undefined,
              calibration: undefined,
              loadedSides: action.loadedSides,
              // #353: a ramp set's load was Metria's proposal for the OLD
              // exercise, not something the athlete wrote: it comes back
              // blank rather than ride onto the new one.
              ...(s.calibration === true
                ? { weight: '', extraNegative: false }
                : isPrefilledWeightSource(s.weightSource)
                ? carried
                  ? { weight: carried.weight, extraNegative: false }
                  : { weight: '', extraNegative: false }
                : {}),
              weightSource:
                s.calibration !== true &&
                isPrefilledWeightSource(s.weightSource) &&
                carried
                  ? carried.source
                  : undefined,
            };
          }),
    };
  });
}
